import { ColorStop, Gradient } from 'fabric';

type FillSolid = { kind: 'solid'; color: string };

type FillLinear = { kind: 'linear'; angle: number; stops: ColorStop[] };

type FillRadial = {
  kind: 'radial';
  stops: ColorStop[];
};

export type Fill = FillSolid | FillLinear | FillRadial;

const RADIAL_COORDS = {
  x1: 0.5,
  y1: 0.5,
  x2: 0.5,
  y2: 0.5,
  r1: 0,
  r2: 0.5
};

// Converts an angle into the gradient's start and end points.
// 0° = left to right, 90° = top to bottom.
const linearCoords = (angle: number) => {
  const rad = (angle * Math.PI) / 180;
  const x = Math.cos(rad) / 2;
  const y = Math.sin(rad) / 2;
  return { x1: 0.5 - x, y1: 0.5 - y, x2: 0.5 + x, y2: 0.5 + y };
};

const coordsToAngle = (c: { x1: number; y1: number; x2: number; y2: number }): number => {
  const angle = Math.round((Math.atan2(c.y2 - c.y1, c.x2 - c.x1) * 180) / Math.PI);
  return ((angle % 360) + 360) % 360;
};

// Converts our Fill type into a value Fabric can render.
export const toFabricFill = (fill: Fill): string | Gradient<'linear'> | Gradient<'radial'> => {
  switch (fill.kind) {
    case 'linear':
      return new Gradient<'linear'>({
        type: 'linear',
        gradientUnits: 'percentage',
        coords: linearCoords(fill.angle),
        colorStops: fill.stops
      });
    case 'radial':
      return new Gradient<'radial'>({
        type: 'radial',
        gradientUnits: 'percentage',
        coords: RADIAL_COORDS,
        colorStops: fill.stops
      });
    case 'solid':
      return fill.color;
  }
};

type GradientLike = {
  type?: string;
  coords?: { x1: number; y1: number; x2: number; y2: number };
  colorStops?: ColorStop[];
};

// Converts our Fill type into a Canvas-compatible fill style.
export const fillToCanvasStyle = (
  ctx: CanvasRenderingContext2D,
  fill: Fill,
  x: number,
  y: number,
  width: number,
  height: number
): string | CanvasGradient => {
  if (fill.kind === 'solid') return fill.color;

  if (fill.kind === 'linear') {
    const { x1, y1, x2, y2 } = linearCoords(fill.angle);
    const grad = ctx.createLinearGradient(x + x1 * width, y + y1 * height, x + x2 * width, y + y2 * height);
    fill.stops.forEach(s => grad.addColorStop(s.offset, s.color));
    return grad;
  }

  const cx = x + width / 2;
  const cy = y + height / 2;
  const r = Math.max(width, height) / 2;
  const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, r);
  fill.stops.forEach(s => grad.addColorStop(s.offset, s.color));
  return grad;
};

// Converts Fabric's stored fill value back into our Fill type.
export const fromFabricFill = (fill: unknown, fallback: string): Fill => {
  if (typeof fill === 'string') return { kind: 'solid', color: fill };

  const g = fill as GradientLike | null;
  if (g && g.coords && Array.isArray(g.colorStops)) {
    if (g.type === 'radial')
      return { kind: 'radial', stops: g.colorStops.map(s => ({ offset: s.offset, color: s.color })) };

    return {
      kind: 'linear',
      angle: coordsToAngle(g.coords),
      stops: g.colorStops.map(s => ({ offset: s.offset, color: s.color }))
    };
  }

  return { kind: 'solid', color: fallback };
};
